import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { usePersonalInfo } from "@/hooks/usePortfolio";
import { SectionTitle } from "@/components/SectionTitle";
import { SocialLinks } from "@/components/SocialLinks";
import { baseApiClient } from "@/api/client";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const { data: p, isLoading, error } = usePersonalInfo();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await baseApiClient.post("/Contact", form);
      if (response.data.success) {
        toast.success(response.data.message || "Message sent successfully!");
        setForm({ name: "", email: "", subject: "", message: "" });
      } else {
        toast.error(response.data.message || "Failed to send message.");
      }
    } catch (err) {
      toast.error("Failed to send message. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (isLoading || error || !p) return null;

  return (
    <section id="contact" className="section-pad relative" data-testid="contact-section">
      <div className="container-x">
        <SectionTitle eyebrow="Connect" title={<>Let's <span className="italic gold-text">build together</span></>} subtitle="Whether it's a consulting engagement, a partnership, or simply a conversation — reach out." />

        <div className="grid lg:grid-cols-12 gap-10">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 space-y-5"
          >
            {[
              { Icon: Mail, label: "Email", value: p.email, href: `mailto:${p.email}` },
              { Icon: Phone, label: "Phone", value: p.phone, href: `tel:${(p.phone || '').replace(/\s/g, "")}` },
              { Icon: MapPin, label: "Location", value: `${p.city}, ${p.state}, ${p.country}` },
            ].map((it, i) => (
              <a
                key={i}
                href={it.href || "#"}
                data-testid={`contact-${it.label.toLowerCase()}`}
                className="block glass rounded-2xl p-6 grain group hover:border-[hsl(var(--gold))]/50 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[hsl(var(--gold))]/10 border border-[hsl(var(--gold))]/30 flex items-center justify-center group-hover:bg-[hsl(var(--gold))]/20 transition-colors">
                    <it.Icon className="w-5 h-5 text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))]" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{it.label}</div>
                    <div className="mt-1 font-medium">{it.value}</div>
                  </div>
                </div>
              </a>
            ))}
            <div className="glass rounded-2xl p-6 grain">
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">Find me on</div>
              <SocialLinks variant="inline" />
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 glass rounded-2xl p-8 grain"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              {[
                { name: "name", label: "Full Name", type: "text", placeholder: "John Doe" },
                { name: "email", label: "Email", type: "email", placeholder: "you@company.com" },
              ].map((f) => (
                <div key={f.name}>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{f.label}</label>
                  <input
                    required
                    type={f.type}
                    name={f.name}
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    placeholder={f.placeholder}
                    data-testid={`contact-input-${f.name}`}
                    className="mt-2 w-full bg-transparent border-b border-[hsl(var(--border))] focus:border-[hsl(var(--gold))] py-3 text-sm outline-none transition-colors"
                  />
                </div>
              ))}
            </div>
            <div className="mt-5">
              <label className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Subject</label>
              <input
                required
                name="subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Business consulting inquiry"
                data-testid="contact-input-subject"
                className="mt-2 w-full bg-transparent border-b border-[hsl(var(--border))] focus:border-[hsl(var(--gold))] py-3 text-sm outline-none transition-colors"
              />
            </div>
            <div className="mt-5">
              <label className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Message</label>
              <textarea
                required
                rows={5}
                name="message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell me about your business or how I can help…"
                data-testid="contact-input-message"
                className="mt-2 w-full bg-transparent border-b border-[hsl(var(--border))] focus:border-[hsl(var(--gold))] py-3 text-sm outline-none transition-colors resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="mt-8 btn-gold px-7 py-3.5 rounded-full text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send Message"}
              <Send className="w-4 h-4" />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
